/**
 * SparkLayer mini-cart / cart drawer regular-price hide (FXT-246).
 *
 * FXT-236 labeled RRP prefixes. Cart still paints a muted regular
 * (`p.text-sm/4`, #333) under par unité. Official Spark token
 * `--spark-cart-rrp-display:none` plus hide of RRP / money-only
 * muted paragraphs. Net / unit price stays.
 *
 * Scope: spark-cart / mini-cart / cart drawer only.
 * Not PDP, not product tiles, not D2C cart, no ATC.
 */
(() => {
  const HOST_SEL = [
    'spark-cart',
    'spark-mini-cart',
    'spark-my-cart',
    'spark-cart-drawer',
    'spark-drawer',
    '#spark-cart',
  ].join(',');
  const DRAWER_SEL = 'cart-drawer-component, dialog.cart-drawer__dialog, .cart-drawer';
  const ROOT_SEL = `${HOST_SEL}, ${DRAWER_SEL}`;
  const EXCLUDE_HOST = new Set(['spark-pdp', 'spark-product-card', 'spark-form']);
  const PRICE_RRP_SEL =
    'p.sl-pricing-rrp-decoration, .sl-pricing-rrp-decoration, spark-product-rrp';
  const PRICE_RRP_MARK = 'data-fixatech-spark-cart-rrp';
  const STYLE_MARK = 'data-fixatech-spark-cart-rrp-style';
  const PRICE_RRP_PREFIX =
    /^(?:prix\s+r[ée]gulier|prix\s+conseill[ée]|prix\s+recommand[ée]|regular\s+price|recommended(?:\s+retail)?\s+price|suggested(?:\s+retail)?\s+price|rrp|msrp)\s*:?\s*/i;
  const UNIT_LABEL = /^(?:par\s+unit[eé]|per\s+unit|\/\s*unit[eé]?)$/i;
  const MONEY_ONLY =
    /^(?:(?:prix\s+r[ée]gulier|regular\s+price)\s*:?\s*)?(?:(?:ca|us|au)\s*\$|\$|€|£)\s*[\d\s.,]+$/i;
  const HAS_MONEY = /(?:(?:ca|us|au)\s*\$|\$|€|£)/i;
  const HIDE_CSS =
    ':host{--spark-cart-rrp-display:none;}' +
    `.sl-pricing-rrp-decoration,spark-product-rrp,[${PRICE_RRP_MARK}]{display:none!important;}`;
  const DEBOUNCE_MS = 120;

  const nextAncestor = (node) => {
    if (!node || node.nodeType !== 1) return null;
    if (node.parentElement) return node.parentElement;
    const root = node.getRootNode && node.getRootNode();
    const host = root && root.host;
    return host && host !== node ? host : null;
  };

  const inExcludedHost = (el) => {
    let node = el;
    while (node && node.nodeType === 1) {
      if (EXCLUDE_HOST.has(node.localName)) return true;
      node = nextAncestor(node);
    }
    return false;
  };

  const inCartScope = (el) => {
    let node = el;
    while (node && node.nodeType === 1) {
      if (EXCLUDE_HOST.has(node.localName)) return false;
      const name = node.localName || '';
      if (
        name === 'spark-cart' ||
        name === 'spark-mini-cart' ||
        name === 'spark-my-cart' ||
        name === 'spark-cart-drawer' ||
        name === 'spark-drawer'
      ) {
        return true;
      }
      if (node.id === 'spark-cart') return true;
      node = nextAncestor(node);
    }
    return false;
  };

  const normText = (el) => (el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim() : '');

  const isNetPrice = (el) =>
    el.classList.contains('font-weight-pricing') && !el.classList.contains('sl-pricing-rrp-decoration');

  const isTextSmMuted = (el) => {
    if (!el || el.localName !== 'p') return false;
    if (el.classList.contains('text-sm/4')) return true;
    const cls = typeof el.className === 'string' ? el.className : '';
    return /(^|\s)text-sm\/4(\s|$)/.test(cls);
  };

  const nearUnitLabel = (el) => {
    const prev = el.previousElementSibling;
    if (prev && UNIT_LABEL.test(normText(prev))) return true;
    const parent = el.parentElement;
    if (!parent || !parent.children) return false;
    for (let i = 0; i < parent.children.length; i += 1) {
      const child = parent.children[i];
      if (child !== el && UNIT_LABEL.test(normText(child))) return true;
    }
    return false;
  };

  const hideNode = (el) => {
    if (!el || el.nodeType !== 1) return;
    el.setAttribute(PRICE_RRP_MARK, '');
    const prev = el.previousElementSibling;
    if (!prev || prev.children.length) return;
    if (PRICE_RRP_PREFIX.test(normText(prev))) prev.setAttribute(PRICE_RRP_MARK, '');
  };

  const injectHideCss = (root) => {
    if (!root || !root.querySelector) return;
    const host = root.host;
    if (host && !inCartScope(host)) return;
    if (!host && root.nodeType === 1 && !inCartScope(root)) return;
    let style = root.querySelector(`style[${STYLE_MARK}]`);
    if (!style) {
      style = document.createElement('style');
      style.setAttribute(STYLE_MARK, '');
    }
    if (style.textContent !== HIDE_CSS) style.textContent = HIDE_CSS;
    if (style.parentNode !== root || root.lastChild !== style) root.appendChild(style);
  };

  const hideRegularPrices = (root) => {
    if (!root || !root.querySelectorAll) return;
    injectHideCss(root);
    root.querySelectorAll(PRICE_RRP_SEL).forEach((el) => {
      if (inExcludedHost(el) || !inCartScope(el)) return;
      hideNode(el);
    });
    root.querySelectorAll('p').forEach((el) => {
      if (inExcludedHost(el) || !inCartScope(el)) return;
      if (isNetPrice(el) || !isTextSmMuted(el)) return;
      const text = normText(el);
      if (!text || UNIT_LABEL.test(text)) return;
      if (!MONEY_ONLY.test(text) && !(HAS_MONEY.test(text) && nearUnitLabel(el))) return;
      hideNode(el);
    });
  };

  const walkRoots = (root, fn) => {
    if (!root) return;
    fn(root);
    if (!root.querySelectorAll) return;
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) walkRoots(el.shadowRoot, fn);
    });
  };

  const paintRoot = (root) => {
    if (!root) return;
    walkRoots(root, hideRegularPrices);
  };

  const pendingHosts = new Set();
  let paintAllPending = false;
  let paintTimer = 0;

  const flushPaint = () => {
    paintTimer = 0;
    if (paintAllPending) {
      paintAllPending = false;
      pendingHosts.clear();
      document.querySelectorAll(ROOT_SEL).forEach((host) => {
        paintRoot(host.shadowRoot || host);
        if (host.shadowRoot) watchHostShadow(host, host.shadowRoot);
      });
      return;
    }
    pendingHosts.forEach((host) => {
      paintRoot(host.shadowRoot || host);
      if (host.shadowRoot) watchHostShadow(host, host.shadowRoot);
    });
    pendingHosts.clear();
  };

  const scheduleFlush = () => {
    if (paintTimer) return;
    paintTimer = setTimeout(flushPaint, DEBOUNCE_MS);
  };

  const scheduleHost = (host) => {
    if (!host) return;
    pendingHosts.add(host);
    scheduleFlush();
  };

  const scheduleAll = () => {
    paintAllPending = true;
    scheduleFlush();
  };

  const waitingShadow = new WeakSet();
  const waitForShadow = (host, cb) => {
    if (host.shadowRoot) {
      if (cb) cb();
      else scheduleHost(host);
      return;
    }
    if (waitingShadow.has(host)) return;
    waitingShadow.add(host);
    const tryOnce = () => {
      if (!host.shadowRoot) return false;
      waitingShadow.delete(host);
      if (cb) cb();
      else scheduleHost(host);
      return true;
    };
    const afterDefined = () => {
      if (tryOnce()) return;
      requestAnimationFrame(() => {
        if (tryOnce()) return;
        setTimeout(() => {
          if (!tryOnce()) waitingShadow.delete(host);
        }, 200);
      });
    };
    const name = host.localName;
    if (window.customElements && customElements.get(name)) afterDefined();
    else if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined(name).then(afterDefined).catch(() => {});
    } else afterDefined();
  };

  const observedRoots = new WeakSet();
  const watchHostShadow = (host, root) => {
    if (!root || observedRoots.has(root)) return;
    observedRoots.add(root);
    new MutationObserver(() => scheduleHost(host)).observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  };

  const watch = (host) => {
    if (!host) return;
    if (host.shadowRoot) {
      paintRoot(host.shadowRoot);
      watchHostShadow(host, host.shadowRoot);
      return;
    }
    paintRoot(host);
    waitForShadow(host, () => {
      paintRoot(host.shadowRoot);
      watchHostShadow(host, host.shadowRoot);
    });
  };

  const hasCartRoots = () => Boolean(document.querySelector(ROOT_SEL));

  const start = () => {
    if (!hasCartRoots()) return;
    scheduleAll();
    document.querySelectorAll(HOST_SEL).forEach((host) => {
      if (host.shadowRoot) watchHostShadow(host, host.shadowRoot);
    });
  };

  const arm = () => {
    start();
  };

  const watchDrawer = () => {
    document.querySelectorAll('cart-drawer-component').forEach((drawer) => {
      if (observedRoots.has(drawer)) return;
      observedRoots.add(drawer);
      new MutationObserver((records) => {
        for (let i = 0; i < records.length; i += 1) {
          const added = records[i].addedNodes;
          for (let j = 0; j < added.length; j += 1) {
            const node = added[j];
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches(HOST_SEL)) watch(node);
            else if (node.querySelector) node.querySelectorAll(HOST_SEL).forEach(watch);
            if (
              (node.matches && node.matches(PRICE_RRP_SEL)) ||
              (node.querySelector && node.querySelector(PRICE_RRP_SEL))
            ) {
              scheduleHost(drawer);
            }
          }
        }
      }).observe(drawer, { childList: true, subtree: true });
      scheduleHost(drawer);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      arm();
      watchDrawer();
    });
  } else {
    arm();
    watchDrawer();
  }

  document.addEventListener('sparklayer:ready', () => {
    arm();
    watchDrawer();
    document.querySelectorAll(HOST_SEL).forEach(watch);
  });

  window.addEventListener('hashchange', () => {
    if ((window.location.hash || '').indexOf('spark-cart') !== -1) scheduleAll();
  });

  if (window.customElements && customElements.whenDefined) {
    ['spark-cart', 'spark-mini-cart', 'spark-my-cart', 'spark-cart-drawer', 'spark-drawer'].forEach((name) => {
      customElements.whenDefined(name).then(arm).catch(() => {});
    });
  }

  if (document.body) {
    new MutationObserver((records) => {
      for (let i = 0; i < records.length; i += 1) {
        const added = records[i].addedNodes;
        for (let j = 0; j < added.length; j += 1) {
          const node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(HOST_SEL)) watch(node);
          else if (node.querySelector) node.querySelectorAll(HOST_SEL).forEach(watch);
        }
      }
    }).observe(document.body, { childList: true });
  }
})();
